import { useCallback, useEffect, useMemo, useState } from 'react';
import { pmGet, pmPost, type PmProgramPlan, type PmPhaseRow, type PmProject } from '../api';
import './BudgetPlan.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Budget Plan — the per-opportunity budget baseline.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/budget.ejs`,
 * markup, copy and CSS values alike. The screen's own description of what it is for, from
 * its help panel:
 *
 *   Set the budget baseline per opportunity. Work top-down: a program-wide Shared bucket
 *   for costs not tied to one opportunity (PMO, shared infrastructure), then each
 *   opportunity by phase.
 *
 * A TREE GRID, NOT CARDS. One table: a Shared/Program bucket plus each opportunity as a
 * group row that expands to its five phase rows. The source rewrote this from cards to a
 * tree grid deliberately — its own "What's new" note says so — so it is reproduced as a
 * grid and not "simplified" back.
 *
 * OVERRIDE NULL ≠ 0. An empty override means "use the proposed figure"; a typed 0 means
 * "this phase costs nothing". The save maps `''` to null, never to 0, and the Effective
 * column tags which of the two it is showing.
 *
 * The shared bucket has no proposal — the resource model is attributed per opportunity —
 * so its Proposed cells render an em dash and its placeholder is `0` rather than
 * `(use proposed)`.
 */

const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};

const fmt = (n: unknown) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

/** Effective = the override when one is set, else the proposal. The source's `effOf`. */
const effOf = (r: PmPhaseRow) =>
  r.override !== null && (r.override as unknown) !== '' ? Number(r.override) : Number(r.proposed) || 0;

const isOverridden = (r: PmPhaseRow) => r.override !== null && (r.override as unknown) !== '';

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/budget.ejs`,
 * its `budgetHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const BUDGET_HELP: HelpContent = {
    title: 'Budget Plan',
    icon: '💰',
    purpose: "Set the <strong>budget baseline per opportunity</strong>. Work top&#8209;down: a project&#8209;wide <strong>Shared</strong> bucket for costs not tied to one opportunity (PMO, shared infrastructure), then <strong>each opportunity</strong> by phase. Each opportunity's per&#8209;phase budget is <strong>proposed automatically</strong> from its resource model, and you can <strong>override</strong> any row.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully budgetable just like any other initiative.",
      "Select a <strong>Project</strong> (delivery project); its Shared bucket and opportunities load below.",
      "In <strong>Shared / Project</strong>, enter project&#8209;level costs per phase (PMO, governance, shared infra).",
      "Open each <strong>opportunity</strong> and accept the proposed per&#8209;phase budget or type a manual <strong>override</strong>.",
      "Click <strong>Save</strong> to lock the baseline the Budget Tracker measures actual spend against."
    ],
    fits: 'RACI Definition &rarr; <strong>Budget Plan</strong> &rarr; Resource Plan &rarr; Execution Control Center &rarr; Budget Tracker',
    firstTime: "Pick a project, review each opportunity's auto-proposed per-phase budget, adjust any row, add shared costs, then Save.",
    sections: [
      {
        label: 'How the budget is structured',
        type: 'defs',
        items: [
          { term: 'Shared', color: '#6a1b9a', def: "Project&#8209;level budget for costs not attributable to one opportunity (PMO, governance, shared infrastructure). Entered manually per phase." },
          { term: 'Opportunity', color: '#1976d2', def: "Each opportunity carries its <strong>own</strong> per&#8209;phase budget — proposed from its resource model, override per row. The project total rolls up Shared + every opportunity." },
          { term: 'Phase', color: '#42a5f5', def: "Every opportunity budgets by the five delivery phases (Assessment → Go&#8209;Live)." }
        ]
      },
      {
        label: 'Proposed vs. override',
        type: 'html',
        html: "The <strong>proposed</strong> figure is derived from that opportunity's <strong>resource model</strong> — allocated <em>FTE × day&#8209;rate × duration</em>. Type an <strong>override</strong> to replace a row; leave it blank to keep the proposal. The <strong>Effective</strong> figure (override ∥ proposed) is what carries forward to the Budget Tracker."
      },
      {
        label: "What's new",
        type: 'html',
        html: "The Budget Plan is a <strong>tree grid</strong>: a Shared/Project bucket plus each opportunity as a <strong>group row</strong> you expand to reveal its five <strong>phase rows</strong> (each marked by its phase-colored box). Type an override on any phase row; the group row shows that scope's Effective total."
      }
    ],
    links: [
      { label: 'Next: Resource Plan →', href: '/project_management/program-management/budget-resources/resource-plan' },
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' }
    ]
  };

export function BudgetPlan() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [currentProject, setCurrentProject] = useState('');
  const [prog, setProg] = useState<PmProgramPlan | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/br/projects')
      .then((d) => setProjects(d.projects ?? []))
      .catch((e) => setError((e as Error).message));
  }, []);

  const onSelect = useCallback(async (id: string) => {
    setCurrentProject(id);
    setError(null);
    if (!id) {
      setProg(null);
      return;
    }
    try {
      const d = await pmGet<PmProgramPlan>(
        `/budget/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setExpanded(new Set());
      setProg(d);
      setDirty(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  /** The phase rows for one scope id — `shared`, or `opp:<id>`. The source's `scopePhases`. */
  const scopePhases = useCallback(
    (id: string): PmPhaseRow[] => {
      if (!prog) return [];
      if (id === 'shared') return prog.shared.phases;
      return prog.opportunities.find((x) => `opp:${x.opportunity_id}` === id)?.phases ?? [];
    },
    [prog],
  );

  const scopeTotal = useCallback(
    (id: string) => scopePhases(id).reduce((s, r) => s + effOf(r), 0),
    [scopePhases],
  );

  const stats = useMemo(() => {
    if (!prog) return null;
    let planned = scopeTotal('shared');
    let proposed = 0;
    let approved = 0;
    prog.opportunities.forEach((op) => {
      planned += scopeTotal(`opp:${op.opportunity_id}`);
      op.phases.forEach((p) => {
        proposed += Number(p.proposed) || 0;
      });
      if (op.approved) approved += Number(op.approved.build_cost) || 0;
    });
    // Variance only exists once there IS an approved cost model to vary from.
    const variance = approved > 0 ? planned - approved : null;
    return { planned, proposed, approved, variance };
  }, [prog, scopeTotal]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = (on: boolean) => {
    if (!prog) return;
    setExpanded(
      on
        ? new Set(['shared', ...prog.opportunities.map((o) => `opp:${o.opportunity_id}`)])
        : new Set(),
    );
  };

  const setOv = (scopeId: string, phase: string, val: string) => {
    const row = scopePhases(scopeId).find((x) => x.phase === phase);
    if (!row) return;
    row.override = val === '' ? null : (val as unknown as number);
    setProg((p) => (p ? { ...p } : p));
    setDirty(true);
  };

  const save = async () => {
    if (!currentProject || !prog) return;
    setSaving(true);
    setSaveLabel('Saving…');
    const amount = (r: PmPhaseRow) =>
      (r.override as unknown) === '' || r.override == null ? null : Number(r.override);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/budget/program-save', {
        delivery_project_id: currentProject,
        shared: prog.shared.phases.map((r) => ({ phase: r.phase, amount: amount(r) })),
        opportunities: prog.opportunities.map((o) => ({
          opportunity_id: o.opportunity_id,
          overrides: o.phases.map((r) => ({ phase: r.phase, amount: amount(r) })),
        })),
      });
      if (d.success) {
        setDirty(false);
        setSaveLabel('Saved ✓');
        setTimeout(() => setSaveLabel('Save'), 1100);
        await onSelect(currentProject);
      } else {
        setError(d.error ?? 'failed');
        setSaveLabel('Save');
      }
    } catch (e) {
      setError((e as Error).message);
      setSaveLabel('Save');
    } finally {
      setSaving(false);
    }
  };

  const plannedTotal = prog
    ? prog.opportunities.reduce(
        (s, o) => s + scopeTotal(`opp:${o.opportunity_id}`),
        scopeTotal('shared'),
      )
    : 0;

  const renderScope = (id: string, title: string, op: PmProgramPlan['opportunities'][number] | null) => {
    const isShared = !op;
    const open = expanded.has(id);
    const accent = isShared ? '#6a1b9a' : '#1976d2';
    const phases = scopePhases(id);
    const prop = phases.reduce((s, r) => s + (Number(r.proposed) || 0), 0);
    const tot = phases.reduce((s, r) => s + effOf(r), 0);

    const rows = [
      <tr className="grp" key={id} onClick={() => toggle(id)}>
        <td className="ind">
          <i className="material-icons pm-gcaret" style={{ transform: `rotate(${open ? 90 : 0}deg)` }}>
            chevron_right
          </i>
        </td>
        <td>
          <div className="pm-scope-cell">
            <span className="pm-dl-dot" style={{ background: accent }} />
            {title}{' '}
            {op ? <span className="pm-dl-oppname">{op.name || ''}</span> : null}{' '}
            {op && op.approved && op.approved.build_cost > 0 ? (
              <span className="pm-dl-pcount" title="Approved cost model">
                appr {fmt(op.approved.build_cost)}
              </span>
            ) : null}
          </div>
        </td>
        <td className="num">{isShared ? '—' : fmt(prop)}</td>
        <td className="num" />
        <td className="num pm-eff">{fmt(tot)}</td>
      </tr>,
    ];

    phases.forEach((r) => {
      const overridden = isOverridden(r);
      rows.push(
        <tr className="phrow" key={`${id}-${r.phase}`} style={{ display: open ? '' : 'none' }}>
          <td className="ind" />
          <td className="ph-cell">
            <span
              className="pm-ph-box"
              style={{ background: PHASE_COLORS[r.phase] || '#b0bec5' }}
              title={r.label}
            />
            <span className="pm-ph-label">{r.label}</span>
          </td>
          {isShared ? (
            <td className="num" style={{ color: '#b0bec5' }}>
              —
            </td>
          ) : (
            <td className="num">
              {fmt(r.proposed)}
              <span className="pm-src-tag">model</span>
            </td>
          )}
          <td className="num">
            <input
              type="number"
              className="pm-ov-input"
              min={0}
              step={1000}
              placeholder={isShared ? '0' : '(use proposed)'}
              value={overridden ? String(r.override) : ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setOv(id, r.phase, e.target.value)}
            />
          </td>
          <td className="num pm-eff">
            {fmt(effOf(r))}
            <span
              className="pm-src-tag"
              style={overridden ? { color: '#1565c0', background: '#e8f1fb' } : undefined}
            >
              {id === 'shared' ? 'shared' : overridden ? 'override' : 'proposed'}
            </span>
          </td>
        </tr>,
      );
    });
    return rows;
  };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <HelpPanel help={withFinalizedRule(BUDGET_HELP)} />
      <div className="pm-bw-head">
        <div className="pm-bw-titleblock">
          <i className="material-icons" style={{ fontSize: '2.1rem', color: '#1976d2' }}>
            account_balance_wallet
          </i>
          <div>
            <h1 className="page-header__title">Budget Plan</h1>
            <p>
              Per-opportunity budget baseline — a Shared bucket plus each opportunity by phase,
              proposed from the resource model.
            </p>
          </div>
        </div>
        <div className="pm-bw-toolbar">
          <div className="pm-dl-fg">
            <SelectField
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={currentProject}
              onChange={(v) => void onSelect(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>
          {prog ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="pm-link-btn" onClick={() => expandAll(true)}>
                Expand all
              </button>
              <button className="pm-link-btn" onClick={() => expandAll(false)}>
                Collapse all
              </button>
              <span style={{ color: '#cfd8dc' }}>|</span>
              <span className="pm-bw-summary">Planned {fmt(plannedTotal)}</span>
              <button className="pm-bw-save" disabled={!dirty || saving} onClick={() => void save()}>
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pm-bw-body">
        {error ? (
          <div className="pm-bw-empty" style={{ color: '#c62828' }}>
            {error}
          </div>
        ) : null}
        {!prog ? (
          <div className="pm-bw-empty">
            Select a program to view its budget by opportunity and phase.
          </div>
        ) : (
          <>
            {stats ? (
              <div className="pm-stat-row">
                <div className="pm-stat">
                  <i className="material-icons ic" style={{ color: '#1976d2' }}>
                    verified
                  </i>
                  <div>
                    <div className="lbl">Approved Cost Model</div>
                    <div className="val">{stats.approved > 0 ? fmt(stats.approved) : '—'}</div>
                    <div className="sub">sum across opportunities</div>
                  </div>
                </div>
                <div className="pm-stat">
                  <i className="material-icons ic" style={{ color: '#43a047' }}>
                    payments
                  </i>
                  <div>
                    <div className="lbl">Planned Budget</div>
                    <div className="val" style={{ color: '#1565c0' }}>
                      {fmt(stats.planned)}
                    </div>
                    <div className="sub">Shared + all opportunities</div>
                  </div>
                </div>
                <div className="pm-stat">
                  <i className="material-icons ic" style={{ color: '#8e24aa' }}>
                    insights
                  </i>
                  <div>
                    <div className="lbl">Resource-derived Proposal</div>
                    <div className="val">{fmt(stats.proposed)}</div>
                    <div className="sub">FTE × rate × duration</div>
                  </div>
                </div>
                {stats.variance != null ? (
                  <div className="pm-stat">
                    <i
                      className="material-icons ic"
                      style={{ color: stats.variance > 0 ? '#e53935' : '#43a047' }}
                    >
                      {stats.variance > 0 ? 'trending_up' : 'trending_down'}
                    </i>
                    <div>
                      <div className="lbl">Variance vs Approved</div>
                      <div
                        className="val"
                        style={{ color: stats.variance > 0 ? '#e53935' : '#43a047' }}
                      >
                        {stats.variance > 0 ? '+' : ''}
                        {fmt(stats.variance)}
                      </div>
                      <div className="sub">planned − approved</div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <table className="pm-bw-grid">
              <colgroup>
                <col style={{ width: 44 }} />
                <col />
                <col style={{ width: 160 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 160 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="ind" />
                  <th>Scope / Phase</th>
                  <th className="num">Proposed</th>
                  <th className="num">Override</th>
                  <th className="num">Effective</th>
                </tr>
              </thead>
              <tbody>
                {renderScope('shared', '🌐 Shared / Program', null)}
                {prog.opportunities.map((op) =>
                  renderScope(`opp:${op.opportunity_id}`, `📌 ${op.opportunity_id}`, op),
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
